import { PeopleIcon, PersonIcon, PlusIcon, TrashIcon } from "@primer/octicons-react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { HttpError } from "../api/client";
import { useAction, useRepoAccess } from "../api/hooks";
import type { GrantRole, RepoGrant } from "../api/types";
import { AvatarImage } from "../components/AvatarImage";
import { Empty, ErrorMessage, Query, TimeAgo } from "../components/common";
import { Subhead } from "../components/SettingsLayout";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardHeader } from "../components/ui/card";
import { ConfirmDialog } from "../components/ui/confirm-dialog";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Select } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { useIsAdmin } from "./Settings";

const ROLES: Array<{ value: GrantRole; label: string }> = [
  { value: "read", label: "Read" },
  { value: "write", label: "Write" },
  { value: "admin", label: "Admin" },
];

/** /:repo/settings/access: users and groups granted a role on the repository, like GitHub collaborators and teams. */
export function SettingsAccess() {
  const { repo = "" } = useParams();
  const admin = useIsAdmin(repo);
  const access = useRepoAccess(repo, admin);
  const [adding, setAdding] = useState(false);
  return (
    <>
      <Subhead title="Access">
        People and groups with a role here, on top of their site role. <b>Read</b> browses, <b>Write</b> opens and lands changes, <b>Admin</b> also manages
        settings and access. Changes to <code>.bee/**</code> stay with site admins.
      </Subhead>
      {admin ? (
        <Card>
          <CardHeader>
            <PeopleIcon /> Members
            <Button type="button" size="sm" variant="primary" className="ml-auto" onClick={() => setAdding(true)}>
              <PlusIcon /> Add member
            </Button>
          </CardHeader>
          <Query q={access}>
            {(rows) =>
              rows.length ? (
                <Table>
                  <TableHeader>
                    <tr>
                      <TableHead>Who</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead className="max-md:hidden">Granted</TableHead>
                      <TableHead />
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {rows.map((g) => (
                      <GrantLine key={`${g.kind}/${g.id}`} repo={repo} g={g} />
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Empty icon={PeopleIcon}>No members added. Everyone has their site role.</Empty>
              )
            }
          </Query>
        </Card>
      ) : null}
      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="top-[10vh]">
          {adding && <GrantForm repo={repo} granted={access.data ?? []} onDone={() => setAdding(false)} />}
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Who the dialog is about to grant; `kind` is null when a repo admin typed a name we cannot look up. */
type Subject = { kind: RepoGrant["kind"] | null; name: string; display?: string; avatar?: string };

const ROLE_HELP: Record<GrantRole, string> = {
  read: "Browse the code, changes and history.",
  write: "Also open and land changes.",
  admin: "Also manage this repository's settings and access.",
};

function GrantForm({ repo, granted, onDone }: { repo: string; granted: RepoGrant[]; onDone: () => void }) {
  const act = useAction(repo);
  const [query, setQuery] = useState("");
  const [subject, setSubject] = useState<Subject | null>(null);
  const [role, setRole] = useState<GrantRole>("write");
  // The user lookup's error, kept so a failed group fallback doesn't hide it.
  const [userError, setUserError] = useState<unknown>(null);
  // Repo admins type an exact name and we try a user, then a group.
  const typed = query.trim();
  const path = (kind: RepoGrant["kind"], name: string) => `/v1/repos/${repo}/access/${kind}/${encodeURIComponent(name)}`;
  const submit = () => {
    if (!subject) return;
    const put = (kind: RepoGrant["kind"], onError?: (e: unknown) => void) =>
      act.mutate({ method: "PUT", path: path(kind, subject.name), body: { role } }, { onSuccess: onDone, onError });
    setUserError(null);
    if (subject.kind) put(subject.kind);
    else
      put("users", (e) => {
        setUserError(e);
        put("groups");
      });
  };
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <div className="border-b border-border-muted px-4 py-3">
        <DialogTitle className="flex items-center gap-2 text-sm font-semibold">
          <PersonIcon /> Add a member
        </DialogTitle>
      </div>
      <div className="flex flex-col gap-4 px-4 py-4">
        {subject ? (
          <div className="flex items-center gap-2 rounded-md border bg-muted px-3 py-2">
            <SubjectLine s={subject} />
            <Button type="button" size="sm" className="ml-auto" onClick={() => setSubject(null)}>
              Change
            </Button>
          </div>
        ) : (
          <div>
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type an exact user or group name"
              aria-label="Find a user or group"
            />
            {typed && (
              <ul className="mt-2 rounded-md border">
                <li>
                  <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted" onClick={() => setSubject({ kind: null, name: typed })}>
                    <PlusIcon size={16} className="text-muted-foreground" /> Add <b>{typed}</b>
                  </button>
                </li>
              </ul>
            )}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="font-semibold">Role</span>
          <Select value={role} options={ROLES} onValueChange={(r) => setRole(r as GrantRole)} aria-label="Role" />
          <span className="text-xs text-muted-foreground">{ROLE_HELP[role]}</span>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border-muted px-4 py-3">
        {act.error ? (
          <div className="min-w-0 flex-1">
            {/* An unknown name fails both lookups; the user error is the one that explains it. */}
            <ErrorMessage error={userError && act.error instanceof HttpError && act.error.status === 404 ? userError : act.error} />
          </div>
        ) : null}
        <Button type="button" onClick={onDone}>
          Cancel
        </Button>
        <Button variant="primary" disabled={act.isPending || !subject}>
          {act.isPending ? "Adding…" : subject ? `Add ${subject.name} as ${role}` : "Add member"}
        </Button>
      </div>
    </form>
  );
}

function SubjectLine({ s }: { s: Subject }) {
  return (
    <span className="inline-flex min-w-0 items-center gap-2">
      {s.kind === "groups" ? <PeopleIcon size={24} className="text-muted-foreground" /> : <AvatarImage src={s.avatar ?? ""} name={s.name} size={24} />}
      <span className="min-w-0 truncate">
        <span className="font-semibold">{s.name}</span>
        {s.display && s.display !== s.name && <span className="text-muted-foreground"> · {s.display}</span>}
      </span>
      {s.kind === "groups" && <Badge>group</Badge>}
    </span>
  );
}

function GrantLine({ repo, g }: { repo: string; g: RepoGrant }) {
  const act = useAction(repo);
  const path = `/v1/repos/${repo}/access/${g.kind}/${encodeURIComponent(g.name)}`;
  return (
    <TableRow>
      <TableCell>
        <span className="inline-flex items-center gap-2">
          {g.kind === "users" ? <AvatarImage src={g.avatar_url} name={g.name} size={24} /> : <PeopleIcon size={20} className="text-muted-foreground" />}
          <span className="min-w-0">
            <span className="font-semibold">{g.name}</span>
            {g.display && g.display !== g.name && <span className="text-muted-foreground"> · {g.display}</span>}
          </span>
          <Badge>
            {g.kind === "users" ? <PersonIcon size={12} /> : <PeopleIcon size={12} />} {g.kind === "users" ? "user" : "group"}
          </Badge>
        </span>
      </TableCell>
      <TableCell>
        <Select
          size="sm"
          value={g.role}
          options={ROLES}
          disabled={act.isPending}
          onValueChange={(role) => act.mutate({ method: "PUT", path, body: { role } })}
          aria-label={`Role of ${g.name}`}
        />
        {act.error ? <ErrorMessage error={act.error} /> : null}
      </TableCell>
      <TableCell className="whitespace-nowrap text-xs text-muted-foreground max-md:hidden">
        <TimeAgo ms={g.granted} /> by {g.granted_by}
      </TableCell>
      <TableCell className="whitespace-nowrap text-right">
        <ConfirmDialog
          title={`Remove ${g.name}'s access?`}
          description={
            g.kind === "users" ? "They keep their site role and any access through their groups." : "Its members keep their site role and their own grants."
          }
          confirmLabel="Remove access"
          onConfirm={() => act.mutate({ method: "DELETE", path, body: {} })}
        >
          <Button type="button" size="sm" variant="danger" disabled={act.isPending} title={`Remove ${g.name}`}>
            <TrashIcon />
          </Button>
        </ConfirmDialog>
      </TableCell>
    </TableRow>
  );
}
